import { FunctionComponent, useEffect, useRef, useState } from "react";
import { faArrowRight, faFlaskVial } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import { AiConfigProspectOption } from "./aiConfig.types";

export interface MappingPreviewDisplayRow {
	id: number;
	sourceLabel: string;
	targetLabel: string;
	value: string | null;
	resolved: boolean;
	isAiMapping: boolean;
	isActive: boolean;
	/** Metadata mismatches (picklist/type/read-only) that Zoho would reject at write time. */
	warnings: string[];
}

export interface MappingPreviewFallbackRow {
	targetLabel: string;
	value: string;
}

export interface MappingPreviewOutcome {
	rows: MappingPreviewDisplayRow[];
	fallbacks: MappingPreviewFallbackRow[];
	/**
	 * CRM fields the sync writes from ids a dry run does not have yet — the account it just
	 * created, the resolved owner, the record type. They are supplied, not missing, so they are
	 * reported separately from both `fallbacks` (which have a concrete value now) and
	 * `missingRequired` (which would fail the write). Optional: only Salesforce reports them.
	 */
	syncResolved?: string[];
	/** Labels of required CRM fields that nothing supplies — a sync would fail. */
	missingRequired: string[];
}

export interface MappingTestSyncRecord {
	label: string;
	url?: string | null;
}

export interface MappingTestSyncOutcome {
	success: boolean;
	error?: string | null;
	records: MappingTestSyncRecord[];
}

export interface MappingPreviewLabels {
	/** Which entity's mapping is being previewed (e.g. "Leads") — shown as a badge. */
	entityBadge: string;
	/** Heading for the "what a sync creates" line (e.g. "A sync will create"). */
	createsLabel: string;
	/** Per-entity sentence describing the real record(s) a sync writes. */
	createsDescription: string;
	cancel: string;
	title: string;
	subtitle: string;
	prospectLabel: string;
	prospectPlaceholder: string;
	run: string;
	source: string;
	target: string;
	value: string;
	emptyValue: string;
	aiNotRun: string;
	inactive: string;
	fallbacksTitle: string;
	fallbacksHint: string;
	/** Only rendered when the binding reports `syncResolved`. */
	syncResolvedTitle?: string;
	syncResolvedHint?: string;
	noMappings: string;
	error: string;
	missingRequiredTitle: string;
	missingRequiredHint: string;
	syncButton: string;
	syncWarning: string;
	syncSuccessTitle: string;
	syncFailedTitle: string;
	syncOpenRecord: string;
}

interface MappingPreviewModalProps {
	isOpen: boolean;
	labels: MappingPreviewLabels;
	onClose: () => void;
	onSearchProspects: (query: string) => Promise<AiConfigProspectOption[]>;
	onCancelSearchProspects?: () => void;
	onRunPreview: (prospectId: number) => Promise<MappingPreviewOutcome>;
	/** Real write: run the standard sync for the selected lead and report the CRM's outcome. */
	onTestSync?: (prospectId: number) => Promise<MappingTestSyncOutcome>;
}

const SEARCH_DEBOUNCE_MS = 600;

/**
 * Dry-run preview for CRM field mappings: pick a real lead, see the values a
 * sync would write per mapping (plus auto-added mandatory fields) without
 * writing to the CRM. Provider-agnostic — the caller supplies the resolved
 * display rows via `onRunPreview`.
 */
export const MappingPreviewModal: FunctionComponent<MappingPreviewModalProps> = ({
	isOpen,
	labels,
	onClose,
	onSearchProspects,
	onCancelSearchProspects,
	onRunPreview,
	onTestSync,
}) => {
	const [prospects, setProspects] = useState<AiConfigProspectOption[]>([]);
	const [selectedProspect, setSelectedProspect] = useState<AiConfigProspectOption | null>(null);
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const [isPreviewLoading, setIsPreviewLoading] = useState(false);
	const [outcome, setOutcome] = useState<MappingPreviewOutcome | null>(null);
	const [testOutcome, setTestOutcome] = useState<MappingTestSyncOutcome | null>(null);
	const [isTestSyncLoading, setIsTestSyncLoading] = useState(false);
	const [error, setError] = useState("");
	const searchTimeoutRef = useRef<number | null>(null);
	const latestSearchRequestRef = useRef(0);

	const prospectItems: AutoCompleteItem[] = prospects.map((prospect) => ({
		id: prospect.id,
		title: prospect.title,
	}));

	useEffect(() => {
		if (!isOpen) {
			setSelectedProspect(null);
			setOutcome(null);
			setTestOutcome(null);
			setError("");
			setProspects([]);
		}
	}, [isOpen]);

	const clearPendingSearchTimeout = () => {
		if (searchTimeoutRef.current) {
			window.clearTimeout(searchTimeoutRef.current);
			searchTimeoutRef.current = null;
		}
	};

	useEffect(() => {
		return () => {
			clearPendingSearchTimeout();
			onCancelSearchProspects?.();
		};
	}, []);

	const searchProspects = (query: string) => {
		clearPendingSearchTimeout();
		onCancelSearchProspects?.();

		if (query.trim().length < 2) {
			setProspects([]);
			setIsSearchLoading(false);

			return;
		}

		const requestId = latestSearchRequestRef.current + 1;

		latestSearchRequestRef.current = requestId;
		setIsSearchLoading(true);
		searchTimeoutRef.current = window.setTimeout(async () => {
			searchTimeoutRef.current = null;

			try {
				const nextProspects = await onSearchProspects(query);

				if (latestSearchRequestRef.current === requestId) {
					setProspects(nextProspects);
				}
			} catch {
				if (latestSearchRequestRef.current === requestId) {
					setProspects([]);
				}
			} finally {
				if (latestSearchRequestRef.current === requestId) {
					setIsSearchLoading(false);
				}
			}
		}, SEARCH_DEBOUNCE_MS);
	};

	const runPreview = async () => {
		if (!selectedProspect) {
			return;
		}

		setIsPreviewLoading(true);
		setError("");
		setTestOutcome(null);
		try {
			setOutcome(await onRunPreview(selectedProspect.id));
		} catch {
			setOutcome(null);
			setError(labels.error);
		} finally {
			setIsPreviewLoading(false);
		}
	};

	const runTestSync = async () => {
		if (!selectedProspect || !onTestSync) {
			return;
		}

		setIsTestSyncLoading(true);
		try {
			setTestOutcome(await onTestSync(selectedProspect.id));
		} catch {
			setTestOutcome({ success: false, error: labels.error, records: [] });
		} finally {
			setIsTestSyncLoading(false);
		}
	};

	const renderRowValue = (row: MappingPreviewDisplayRow) => (
		<div>
			{row.resolved ? (
				<span className="font-medium text-text break-words">{row.value}</span>
			) : (
				<span className="italic text-text-meta">
					{row.isAiMapping ? labels.aiNotRun : labels.emptyValue}
				</span>
			)}
			{row.warnings.map((warning) => (
				<div key={warning} className="mt-0.5 text-xs text-status-warning">
					{warning}
				</div>
			))}
		</div>
	);

	const renderTestSyncResult = (result: MappingTestSyncOutcome) => (
		<div
			className={`mt-3 rounded-xl border px-3 py-2 text-sm ${
				result.success
					? "border-status-success bg-status-success-light text-text"
					: "border-status-error bg-status-error-light text-text"
			}`}
			data-testid="mapping-preview-sync-result"
		>
			<div className="font-semibold">
				{result.success ? labels.syncSuccessTitle : labels.syncFailedTitle}
			</div>
			{result.error && <div className="mt-1 break-words text-xs">{result.error}</div>}
			{result.records.map((record) => (
				<div key={record.label} className="mt-1 flex items-center gap-2 text-xs">
					<span>{record.label}</span>
					{record.url && (
						<a
							className="font-semibold text-brand-light underline cursor-pointer"
							href={record.url}
							rel="noreferrer"
							target="_blank"
						>
							{labels.syncOpenRecord}
						</a>
					)}
				</div>
			))}
		</div>
	);

	const footer = (
		<>
			<Button color={ButtonColor.ACTION_SECONDARY} title={labels.cancel} onClick={onClose} />
			<div className="flex items-center gap-3">
				<Button
					color={ButtonColor.BRAND}
					disabled={!selectedProspect || isPreviewLoading}
					isLoading={isPreviewLoading}
					title={labels.run}
					onClick={runPreview}
				/>
				{onTestSync && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={!selectedProspect || isTestSyncLoading}
						isLoading={isTestSyncLoading}
						title={labels.syncButton}
						onClick={runTestSync}
					/>
				)}
			</div>
		</>
	);

	return (
		<Modal
			footer={footer}
			handleClose={onClose}
			icon={<Icon className="w-5 h-5 text-brand-light" icon={faFlaskVial} />}
			isOpened={isOpen}
			size="sm"
			title={labels.title}
			zIndex={1100}
		>
			<div className="space-y-4" data-testid="mapping-preview-modal">
				<div className="text-xs leading-5 text-text-meta">{labels.subtitle}</div>

				<div
					className="flex flex-col items-start gap-1.5 rounded-xl border border-border-light bg-background px-3 py-2.5"
					data-testid="mapping-preview-entity-context"
				>
					<span className="rounded-full border border-border-light bg-white px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-light">
						{labels.entityBadge}
					</span>
					<div className="text-xs leading-5 text-text-meta">
						<span className="font-semibold text-text">{labels.createsLabel}:</span>{" "}
						{labels.createsDescription}
					</div>
				</div>

				<AutoComplete
					data={prospectItems}
					disableLocalFilter
					handleReset={() => {
						setSelectedProspect(null);
						setOutcome(null);
						setError("");
					}}
					handleSearch={searchProspects}
					handleSelect={(item) => {
						const matched = item ? prospects.find((p) => p.id === Number(item.id)) : null;

						setSelectedProspect(matched ?? null);
						setOutcome(null);
						setError("");
					}}
					isLoading={isSearchLoading}
					label={labels.prospectLabel}
					placeholder={labels.prospectPlaceholder}
					selectedItem={selectedProspect?.title ?? ""}
				/>

				{error && (
					<div className="text-xs text-status-error" data-testid="mapping-preview-error">
						{error}
					</div>
				)}

				{outcome && (
					<div data-testid="mapping-preview-result">
						<div className="flex items-center gap-3 py-2 border-b border-border-light text-sm font-medium text-text-meta">
							<div className="flex-1">{labels.source}</div>
							<div className="w-[24px]" />
							<div className="flex-1">{labels.target}</div>
							<div className="flex-1">{labels.value}</div>
						</div>
						{outcome.rows.map((row) => (
							<div
								key={row.id}
								className="flex items-center gap-3 py-2 border-b border-border-light text-sm"
								data-testid={`mapping-preview-row-${row.id}`}
							>
								<div className="flex-1 text-text">
									{row.sourceLabel}
									{!row.isActive && (
										<span className="ml-2 rounded-full border border-border-light bg-background px-2 py-0.5 text-xs text-text-meta">
											{labels.inactive}
										</span>
									)}
								</div>
								<div className="w-[24px] flex items-center justify-center">
									<Icon className="w-[14px] h-[14px] text-gray-400" icon={faArrowRight} />
								</div>
								<div className="flex-1 text-text">{row.targetLabel}</div>
								<div className="flex-1">{renderRowValue(row)}</div>
							</div>
						))}
						{outcome.rows.length === 0 && (
							<div className="py-6 text-center text-sm text-text-meta">{labels.noMappings}</div>
						)}

						{outcome.fallbacks.length > 0 && (
							<div className="mt-4" data-testid="mapping-preview-fallbacks">
								<div className="text-sm font-semibold text-text">{labels.fallbacksTitle}</div>
								<div className="mt-1 text-xs leading-5 text-text-meta">{labels.fallbacksHint}</div>
								{outcome.fallbacks.map((fallback) => (
									<div
										key={fallback.targetLabel}
										className="flex items-center gap-3 py-2 border-b border-border-light text-sm"
									>
										<div className="flex-1 text-text">{fallback.targetLabel}</div>
										<div className="flex-1 font-medium text-text break-words">{fallback.value}</div>
									</div>
								))}
							</div>
						)}

						{!!outcome.syncResolved?.length && (
							<div className="mt-4" data-testid="mapping-preview-sync-resolved">
								<div className="text-sm font-semibold text-text">{labels.syncResolvedTitle}</div>
								<div className="mt-1 text-xs leading-5 text-text-meta">
									{labels.syncResolvedHint}
								</div>
								<div className="mt-1 text-xs font-semibold text-text">
									{outcome.syncResolved.join(", ")}
								</div>
							</div>
						)}

						{outcome.missingRequired.length > 0 && (
							<div
								className="mt-4 rounded-xl border border-status-warning bg-status-warning-light px-3 py-2"
								data-testid="mapping-preview-missing-required"
							>
								<div className="text-sm font-semibold text-text">{labels.missingRequiredTitle}</div>
								<div className="mt-1 text-xs leading-5 text-text">{labels.missingRequiredHint}</div>
								<div className="mt-1 text-xs font-semibold text-text">
									{outcome.missingRequired.join(", ")}
								</div>
							</div>
						)}
					</div>
				)}

				{onTestSync && selectedProspect && (
					<div
						className="text-xs leading-5 text-text-meta"
						data-testid="mapping-preview-sync-warning"
					>
						{labels.syncWarning}
					</div>
				)}

				{testOutcome && renderTestSyncResult(testOutcome)}
			</div>
		</Modal>
	);
};
